import {
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { SurveyState } from '../../../../data/gql-gen/dashboard/graphql'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { useBaseMarketLabel } from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { ProjectFlowBreadcrumb } from '../ProjectFlowBreadcrumb'
import { useMarketsCompletes } from '../ProjectPage/ProjectPage.hooks'
import { getMarkets, isProjectInProgress } from '../markets.utils'
import { MarketFieldworkCard } from './MarketFieldworkCard'

// Status filter pills. `state: null` is the catch-all "All".
const STATUS_FILTERS: { label: string; state: SurveyState | null }[] = [
  { label: 'All', state: null },
  { label: 'Live', state: SurveyState.Live },
  { label: 'Paused', state: SurveyState.Paused },
  { label: 'Complete', state: SurveyState.Completed },
  { label: 'Draft', state: SurveyState.Draft }
]

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  filters: { display: 'flex', gap: 8 },
  // reset so a native (accessible) <button> can be styled as a pill
  pill: {
    background: '#FFFFFF',
    border: '1px solid #E5EAF5',
    borderRadius: 999,
    padding: '6px 16px',
    cursor: 'pointer',
    color: '#42526E'
  },
  pillActive: {
    background: 'var(--primary)',
    borderColor: 'var(--primary)',
    color: '#FFFFFF'
  },
  empty: { padding: '24px 0', color: '#6B7280' }
})

/**
 * The Fieldwork step of the multi-market flow: one card per market showing
 * overall completion (collected / target) with an expandable per-market quota
 * breakdown (age, gender, …). A status filter narrows the list to markets in a
 * given state. The route is project-scoped (no surveyId); the markets are all
 * the project's surveys, the base/master included.
 */
export const FieldworkPage = () => {
  const { classes, cx } = useStyles()
  const projectId = useProjectId()
  const { project, loading } = useGetProjectByProjectId()
  const baseMarket = useBaseMarketLabel()
  const [statusFilter, setStatusFilter] = useState<SurveyState | null>(null)

  const markets = getMarkets(project)
  const surveyIds = markets.map((survey) => survey.surveyId)
  const { completesBySurveyId } = useMarketsCompletes(surveyIds)

  // Fieldwork data (quota fill, collected completes) only exists once a market
  // has launched; the breadcrumb hides this step pre-launch, so anything
  // arriving here (deep link, stale bookmark) is sent back to the dashboard.
  if (!loading && project && !isProjectInProgress(project)) {
    return <Navigate to={`/project/${projectId}`} replace />
  }

  const visibleMarkets = statusFilter
    ? markets.filter((survey) => survey.state === statusFilter)
    : markets

  return (
    <div>
      <ProjectFlowBreadcrumb activeStep="Fieldwork" />

      <div className={classes.page}>
        <div className={classes.header}>
          <Text size={TextSize.xl} weight={TextWeight.Bold}>
            Fieldwork
          </Text>
          <div className={classes.filters}>
            {STATUS_FILTERS.map(({ label, state }) => (
              <button
                key={label}
                type="button"
                className={cx(
                  classes.pill,
                  state === statusFilter && classes.pillActive
                )}
                onClick={() => setStatusFilter(state)}
              >
                <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                  {label}
                </Text>
              </button>
            ))}
          </div>
        </div>

        {markets.length === 0 ? (
          <div className={classes.empty}>
            <Text size={TextSize.ms}>
              No markets yet. Add a market on the Audiences step.
            </Text>
          </div>
        ) : visibleMarkets.length === 0 ? (
          <div className={classes.empty}>
            <Text size={TextSize.ms}>No markets match this filter.</Text>
          </div>
        ) : (
          visibleMarkets.map((survey) => (
            <MarketFieldworkCard
              key={survey.surveyId}
              survey={survey}
              displayName={
                baseMarket && survey.surveyId === baseMarket.surveyId
                  ? baseMarket.label
                  : undefined
              }
              collected={
                completesBySurveyId.get(survey.surveyId)?.collected ?? 0
              }
              target={completesBySurveyId.get(survey.surveyId)?.target ?? 0}
            />
          ))
        )}
      </div>
    </div>
  )
}
